import React, { useState } from 'react';
import { colors, borders, fonts } from './tokens';

export interface FocusContribution {
    dim: string;
    pts: number;
    color: string;
}

interface FocusCardProps {
    title: string;
    tag?: string;
    eyebrowMeta?: string; // e.g. "TODAY · APRIL 25 · 5 MIN"
    description?: string;
    howSteps?: string[]; // ordered steps
    why?: string;
    whyMore?: string; // expanded "show more" text
    contributions?: FocusContribution[];
    isCompleted: boolean;
    onMarkDone: () => void;
    onSwap?: () => void;
    primaryLabel?: string;
    primaryDoneLabel?: string;
    secondaryLabel?: string;
    howLabel?: string;
    whyLabel?: string;
    contributesLabel?: string;
    showMoreLabel?: string;
    showLessLabel?: string;
    ptsUnit?: string;
}

const FocusCard: React.FC<FocusCardProps> = ({
    title,
    tag,
    eyebrowMeta,
    description,
    howSteps,
    why,
    whyMore,
    contributions,
    isCompleted,
    onMarkDone,
    onSwap,
    primaryLabel = 'Mark as done',
    primaryDoneLabel = 'Done ✓',
    secondaryLabel = 'Swap task',
    howLabel = 'HOW TO DO',
    whyLabel = 'WHY THIS WORKS',
    contributesLabel = 'CONTRIBUTES',
    showMoreLabel = '+ Show more',
    showLessLabel = '- Show less',
    ptsUnit = 'pts',
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            style={{
                borderRadius: 20,
                background: colors.cardBg,
                border: borders.hairline,
                padding: '32px 36px',
                display: 'flex',
                flexDirection: 'column',
                gap: 22,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {eyebrowMeta && (
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: '0.22em',
                            color: colors.muted,
                        }}
                    >
                        {eyebrowMeta}
                    </div>
                )}
                {tag && (
                    <span
                        style={{
                            fontSize: 10,
                            color: colors.muted,
                            padding: '3px 8px',
                            border: borders.hair2,
                            borderRadius: 999,
                            fontFamily: fonts.mono,
                            letterSpacing: '0.1em',
                        }}
                    >
                        {tag.toUpperCase()}
                    </span>
                )}
            </div>

            <div
                style={{
                    fontSize: 30,
                    fontWeight: 400,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15,
                    color: colors.textPrimary,
                }}
            >
                {title}
            </div>

            {description && !howSteps?.length && (
                <p style={{ margin: 0, fontSize: 14, color: '#d8d6d0', lineHeight: 1.6 }}>{description}</p>
            )}

            {howSteps && howSteps.length > 0 && (
                <div
                    className="dash-edit__label-row"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '120px 1fr',
                        gap: 18,
                        paddingTop: 6,
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: '0.18em',
                            color: colors.muted,
                            lineHeight: '24px',
                        }}
                    >
                        {howLabel}
                    </div>
                    <div
                        style={{
                            fontSize: 14,
                            color: '#d8d6d0',
                            lineHeight: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                    >
                        {howSteps.map((step) => (
                            <div key={step}>{step}</div>
                        ))}
                    </div>
                </div>
            )}

            {why && (
                <div className="dash-edit__label-row" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18 }}>
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: '0.18em',
                            color: colors.muted,
                            lineHeight: '24px',
                        }}
                    >
                        {whyLabel}
                    </div>
                    <div>
                        <div style={{ fontSize: 14, color: '#d8d6d0', lineHeight: '24px' }}>
                            {why}
                            {whyMore && expanded && <span> {whyMore}</span>}
                        </div>
                        {whyMore && (
                            <button
                                type="button"
                                onClick={() => setExpanded((e) => !e)}
                                style={{
                                    marginTop: 12,
                                    background: 'transparent',
                                    color: colors.muted,
                                    border: 'none',
                                    padding: 0,
                                    fontSize: 13,
                                    fontFamily: 'inherit',
                                    cursor: 'pointer',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                {expanded ? showLessLabel : showMoreLabel}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {contributions && contributions.length > 0 && (
                <div className="dash-edit__label-row" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 18 }}>
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: '0.18em',
                            color: colors.muted,
                            paddingTop: 6,
                        }}
                    >
                        {contributesLabel}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {contributions.map((c) => (
                            <div
                                key={c.dim}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '7px 12px 7px 10px',
                                    background: `${c.color}14`,
                                    border: `1px solid ${c.color}33`,
                                    borderRadius: 999,
                                    fontSize: 12,
                                }}
                            >
                                <span
                                    style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        background: c.color,
                                        boxShadow: `0 0 6px ${c.color}`,
                                    }}
                                />
                                <span style={{ color: colors.textPrimary, fontWeight: 500 }}>{c.dim}</span>
                                <span
                                    style={{
                                        color: c.color,
                                        fontFamily: fonts.mono,
                                        fontSize: 11,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    +{c.pts} {ptsUnit}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginTop: 4,
                }}
            >
                <button
                    type="button"
                    onClick={onMarkDone}
                    style={{
                        background: isCompleted ? '#84cc16' : '#f4f2ee',
                        color: '#0a0a0a',
                        border: 'none',
                        padding: '13px 24px',
                        borderRadius: 999,
                        fontWeight: 500,
                        fontSize: 14,
                        letterSpacing: '-0.01em',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                    }}
                >
                    {isCompleted ? primaryDoneLabel : primaryLabel}
                </button>
                {onSwap && (
                    <button
                        type="button"
                        onClick={onSwap}
                        style={{
                            background: 'transparent',
                            color: colors.muted,
                            border: borders.hairline,
                            padding: '13px 22px',
                            borderRadius: 999,
                            fontWeight: 400,
                            fontSize: 14,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        {secondaryLabel}
                    </button>
                )}
            </div>
        </div>
    );
};

export default FocusCard;
